import { rejectionReasonLabel } from '../../../../../../constants/redeProto';
import { SectionCard } from '../../components/SectionCard';
import { StateFeedback } from '../../components/StateFeedback';
import {
  HeatBadge,
  Table,
  TableWrap,
} from '../../components/StatesTable/styles';
import { StatusPill } from '../../components/StatusPill';
import {
  classificationColorOf,
  csatColor,
  heatColor,
} from '../../theme/tokens';
import { scoreToClassification } from '../../utils/classify';
import {
  EMPTY_VALUE,
  formatCsat,
  formatInt,
  formatPct,
} from '../../utils/format';
import {
  CardHead,
  CardMetrics,
  CardName,
  CardSub,
  Kpi,
  KpiLabel,
  KpiRow,
  KpiValue,
  ManagerCard,
  ManagerGrid,
  Nota,
  Subsection,
  SubTitle,
} from './styles';
import useCustomerSuccess from './useCustomerSuccess';

export function CustomerSuccess(): JSX.Element {
  const { managers, selectedId, selectCs, detail, reasons, loading, error } =
    useCustomerSuccess();

  const selectedManager = detail?.manager;

  return (
    <StateFeedback
      loading={loading}
      error={error}
      empty={managers.length === 0}
    >
      <SectionCard
        title="Customer Success Managers"
        subtitle={`${managers.length} responsáveis · clique para ver detalhes`}
      >
        <ManagerGrid>
          {managers.map((manager) => {
            const classification = scoreToClassification(
              manager.overallScore ?? 0
            );
            return (
              <ManagerCard
                key={manager.id}
                type="button"
                selected={selectedId === manager.id}
                onClick={() => selectCs(manager.id)}
              >
                <CardHead>
                  <span>
                    <CardName>{manager.name}</CardName>
                    <br />
                    <CardSub>
                      {manager.region ?? EMPTY_VALUE} ·{' '}
                      {manager.territories ?? EMPTY_VALUE}
                    </CardSub>
                  </span>
                  <Nota color={classificationColorOf(classification)}>
                    {manager.overallScore == null
                      ? EMPTY_VALUE
                      : Math.round(manager.overallScore)}
                  </Nota>
                </CardHead>
                <CardMetrics>
                  <span>
                    Polos <strong>{manager.polesCount}</strong>
                  </span>
                  <span>
                    Cred. <strong>{manager.accredited}</strong>
                  </span>
                  <span>
                    SLA <strong>{formatPct(manager.sla)}</strong>
                  </span>
                  <span>
                    CSAT <strong>{formatCsat(manager.csat)}</strong>
                  </span>
                </CardMetrics>
                <StatusPill classification={classification} />
              </ManagerCard>
            );
          })}
        </ManagerGrid>
      </SectionCard>

      {selectedManager && (
        <Subsection>
          <SectionCard
            title={`CS · ${selectedManager.name}`}
            subtitle={`${selectedManager.region ?? EMPTY_VALUE} · ${
              selectedManager.territories ?? EMPTY_VALUE
            }`}
          >
            <KpiRow>
              <Kpi>
                <KpiLabel>Demandas</KpiLabel>
                <KpiValue>{formatInt(selectedManager.demands)}</KpiValue>
              </Kpi>
              <Kpi>
                <KpiLabel>SLA</KpiLabel>
                <KpiValue color={heatColor(selectedManager.sla)}>
                  {formatPct(selectedManager.sla)}
                </KpiValue>
              </Kpi>
              <Kpi>
                <KpiLabel>Aprovação</KpiLabel>
                <KpiValue color={heatColor(selectedManager.approval)}>
                  {formatPct(selectedManager.approval)}
                </KpiValue>
              </Kpi>
              <Kpi>
                <KpiLabel>CSAT</KpiLabel>
                <KpiValue color={csatColor(selectedManager.csat)}>
                  {formatCsat(selectedManager.csat)}
                </KpiValue>
              </Kpi>
              <Kpi>
                <KpiLabel>Credenciadas</KpiLabel>
                <KpiValue>{formatInt(selectedManager.accredited)}</KpiValue>
              </Kpi>
              <Kpi>
                <KpiLabel>Polos</KpiLabel>
                <KpiValue>{formatInt(selectedManager.polesCount)}</KpiValue>
              </Kpi>
            </KpiRow>

            <Subsection>
              <SubTitle>Polos acompanhados</SubTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Polo</th>
                      <th>UF</th>
                      <th className="num">SLA</th>
                      <th className="num">CSAT</th>
                      <th className="num">Aprovação</th>
                      <th className="num">Atrasos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail?.poles ?? []).map((pole) => (
                      <tr key={pole.poleId}>
                        <td>{pole.name ?? pole.city}</td>
                        <td>{pole.uf}</td>
                        <td className="num">
                          <HeatBadge color={heatColor(pole.sla)}>
                            {formatPct(pole.sla)}
                          </HeatBadge>
                        </td>
                        <td className="num">
                          <HeatBadge color={csatColor(pole.csat)}>
                            {formatCsat(pole.csat)}
                          </HeatBadge>
                        </td>
                        <td className="num">{formatPct(pole.approval)}</td>
                        <td className="num">{formatInt(pole.delayed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </Subsection>

            <Subsection>
              <SubTitle>Credenciadas</SubTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Credenciada</th>
                      <th>UF</th>
                      <th className="num">Demandas</th>
                      <th className="num">SLA</th>
                      <th className="num">Aprovação</th>
                      <th className="num">CSAT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail?.accredited ?? []).map((company) => (
                      <tr key={company.licensedCompanyId}>
                        <td>{company.name ?? EMPTY_VALUE}</td>
                        <td>{company.uf ?? EMPTY_VALUE}</td>
                        <td className="num">{formatInt(company.demands)}</td>
                        <td className="num">{formatPct(company.sla)}</td>
                        <td className="num">{formatPct(company.approval)}</td>
                        <td className="num">{formatCsat(company.csat)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </Subsection>
          </SectionCard>
        </Subsection>
      )}

      <Subsection>
        <SectionCard
          title="Top motivos de rejeição"
          subtitle="Onde focar capacitação e retrabalho"
        >
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Motivo</th>
                  <th className="num">Ocorrências</th>
                  <th className="num">% do total</th>
                </tr>
              </thead>
              <tbody>
                {reasons.map((reason) => (
                  <tr key={reason.reason}>
                    <td>{rejectionReasonLabel(reason.reason)}</td>
                    <td className="num">{formatInt(reason.occurrences)}</td>
                    <td className="num">{formatPct(reason.pctOfTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </SectionCard>
      </Subsection>
    </StateFeedback>
  );
}
